import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Chip,
  CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import ClassOutlinedIcon from '@mui/icons-material/ClassOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar
} from 'recharts';
import { DataGrid } from '@mui/x-data-grid';
import { getUser } from '../../lib/auth';
import { authGet } from '../../lib/api';
import SavedColumnViewControls, { useSavedColumnView } from '../../components/SavedColumnViewControls';
import { formatDate, formatDateTime as formatFullDateTime, parseDbDate } from '../../lib/dateFormat';

const fallbackColors = [
  '#1976d2',
  '#9c27b0',
  '#2e7d32',
  '#ed6c02',
  '#6d4c41',
  '#00838f',
  '#5e35b1',
  '#c62828'
];

function formatTimeLabel(time24) {
  if (!time24) return '';
  const [hourStr, minute] = String(time24).slice(0, 5).split(':');
  const hour = Number(hourStr);
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${minute} ${suffix}`;
}

function formatDateLabel(dateValue) {
  return formatDate(dateValue, '—');
}

function formatDateTime(dateValue, timeValue) {
  const date = formatDateLabel(dateValue);
  const time = formatTimeLabel(timeValue);
  return time ? `${date} at ${time}` : date;
}

function formatPhone(value) {
  if (!value) return '';
  const digits = String(value).replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export default function Dashboard() {
  const user = getUser();
  const [dateFilter, setDateFilter] = useState('month');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [scheduledClasses, setScheduledClasses] = useState([]);
  const [students, setStudents] = useState([]);

  useEffect(() => {
    const loadDashboardData = async () => {
      setLoading(true);
      try {
        const [scheduledData, studentsData] = await Promise.all([
          authGet('/api/scheduled-classes'),
          authGet('/api/students')
        ]);

        setScheduledClasses(scheduledData.scheduledClasses || []);
        setStudents(studentsData.students || []);
      } catch (err) {
        console.error('Load dashboard data error:', err);
        setScheduledClasses([]);
        setStudents([]);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  const now = new Date();

  const cutoff = useMemo(() => {
    const current = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0).getTime();

    switch (dateFilter) {
      case 'day':
        return current - 1 * 86400000;
      case 'week':
        return current - 7 * 86400000;
      case 'month':
        return current - 30 * 86400000;
      default:
        return 0;
    }
  }, [dateFilter, now]);

  const colorMap = useMemo(() => {
    const classNames = [...new Set(scheduledClasses.map((item) => item.class_name).filter(Boolean))];
    const map = {};
    classNames.forEach((name, index) => {
      map[name] = fallbackColors[index % fallbackColors.length];
    });
    return map;
  }, [scheduledClasses]);

  const filteredSessions = useMemo(() => {
    return scheduledClasses.filter((session) => {
      const sessionDate = parseDbDate(session.class_date);
      const sessionTime = sessionDate ? sessionDate.getTime() : 0;
      const matchesDate = sessionTime >= cutoff;

      const haystack = [
        session.class_name,
        session.class_code,
        session.instructor_name,
        session.location,
        session.notes
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const matchesSearch = !search || haystack.includes(search.toLowerCase());

      return matchesDate && matchesSearch;
    });
  }, [scheduledClasses, cutoff, search]);

  const filteredStudents = useMemo(() => {
    return students.filter((student) => {
      const createdAt = student.created_at ? new Date(student.created_at).getTime() : 0;
      const matchesDate = createdAt >= cutoff;

      const haystack = [
        student.first_name,
        student.middle_name,
        student.last_name,
        student.code,
        student.email,
        student.driver_number,
        student.phone
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const matchesSearch = !search || haystack.includes(search.toLowerCase());

      return matchesDate && matchesSearch;
    });
  }, [students, cutoff, search]);

  const totalClasses = filteredSessions.length;
  const totalStudents = filteredSessions.reduce(
    (sum, item) => sum + Number(item.student_count || 0),
    0
  );
  const activePrograms = new Set(filteredSessions.map((item) => item.class_name).filter(Boolean)).size;
  const avgClassSize = totalClasses ? (totalStudents / totalClasses).toFixed(1) : '0.0';

  const stats = [
    {
      key: 'classes',
      icon: <EventOutlinedIcon />,
      value: totalClasses,
      label: 'Scheduled Classes',
      color: 'primary'
    },
    {
      key: 'students',
      icon: <SchoolOutlinedIcon />,
      value: totalStudents,
      label: 'Students Enrolled',
      color: 'secondary'
    },
    {
      key: 'programs',
      icon: <ClassOutlinedIcon />,
      value: activePrograms,
      label: 'Active Class Types',
      color: 'success'
    },
    {
      key: 'avg',
      icon: <GroupsOutlinedIcon />,
      value: avgClassSize,
      label: 'Avg Class Size',
      color: 'warning'
    }
  ];

  const classesByDay = useMemo(() => {
    const grouped = {};

    filteredSessions.forEach((session) => {
      const d = parseDbDate(session.class_date);
      if (!d) return;

      const day = formatDate(d);

      if (!grouped[day]) {
        grouped[day] = { day, classes: 0, students: 0 };
      }

      grouped[day].classes += 1;
      grouped[day].students += Number(session.student_count || 0);
    });

    return Object.values(grouped);
  }, [filteredSessions]);

  const classMix = useMemo(() => {
    const grouped = {};

    filteredSessions.forEach((session) => {
      const key = session.class_name || 'Unknown';
      if (!grouped[key]) grouped[key] = 0;
      grouped[key] += 1;
    });

    return Object.entries(grouped).map(([name, value]) => ({
      name,
      value
    }));
  }, [filteredSessions]);

  const enrollmentByClass = useMemo(() => {
    const grouped = {};

    filteredSessions.forEach((session) => {
      const key = session.class_name || 'Unknown';
      if (!grouped[key]) grouped[key] = 0;
      grouped[key] += Number(session.student_count || 0);
    });

    return Object.entries(grouped).map(([name, studentsCount]) => ({
      name,
      students: studentsCount
    }));
  }, [filteredSessions]);

  const upcomingSessions = useMemo(() => {
    const currentTs = now.getTime();

    return [...filteredSessions]
      .filter((session) => {
        const d = parseDbDate(session.class_date);
        return d && d.getTime() >= currentTs - 86400000;
      })
      .sort((a, b) => {
        const aDate = parseDbDate(a.class_date)?.getTime() || 0;
        const bDate = parseDbDate(b.class_date)?.getTime() || 0;
        if (aDate !== bDate) return aDate - bDate;
        return String(a.class_time || '').localeCompare(String(b.class_time || ''));
      })
      .slice(0, 5);
  }, [filteredSessions, now]);

  const sessionRows = filteredSessions.map((session) => ({
    id: session.id,
    class_name: session.class_name,
    instructor_name: session.instructor_name || 'Unassigned',
    location: session.location || '—',
    date: formatDateLabel(session.class_date),
    time: formatTimeLabel(session.class_time),
    students: Number(session.student_count || 0),
    status: 'Scheduled'
  }));

  const studentRows = filteredStudents
    .slice()
    .sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime())
    .slice(0, 10)
    .map((student) => ({
      id: student.id,
      name: `${student.first_name || ''} ${student.last_name || ''}`.trim(),
      code: student.code || '—',
      phone: formatPhone(student.phone) || '—',
      created: formatFullDateTime(student.created_at, { fallback: '—' })
    }));

  const sessionColumns = [
    { field: 'class_name', headerName: 'Class', flex: 1.2 },
    { field: 'instructor_name', headerName: 'Instructor', flex: 1.1 },
    { field: 'location', headerName: 'Location', flex: 1 },
    { field: 'date', headerName: 'Date', flex: 1 },
    { field: 'time', headerName: 'Time', flex: 0.9 },
    { field: 'students', headerName: 'Students', flex: 0.8 },
    { field: 'status', headerName: 'Status', flex: 0.9 }
  ];

  const studentColumns = [
    { field: 'name', headerName: 'Student', flex: 1.4 },
    { field: 'code', headerName: 'Code', flex: 0.9 },
    { field: 'phone', headerName: 'Phone', flex: 1.1 },
    { field: 'created', headerName: 'Created', flex: 1.4 }
  ];

  const {
    columnVisibilityModel: sessionColumnVisibilityModel,
    setColumnVisibilityModel: setSessionColumnVisibilityModel,
    setFieldVisible: setSessionFieldVisible,
    showSimpleView: showSessionSimpleView,
    showFullView: showSessionFullView,
    resetView: resetSessionView
  } = useSavedColumnView('dashboard-schedule', sessionColumns, ['class_name', 'instructor_name', 'date', 'time', 'students']);

  const {
    columnVisibilityModel: studentColumnVisibilityModel,
    setColumnVisibilityModel: setStudentColumnVisibilityModel,
    setFieldVisible: setStudentFieldVisible,
    showSimpleView: showStudentSimpleView,
    showFullView: showStudentFullView,
    resetView: resetStudentView
  } = useSavedColumnView('dashboard-students', studentColumns, ['name', 'code', 'phone']);

  return (
    <Box p={4}>
      <Box display="flex" justifyContent="space-between" mb={3} gap={2} flexWrap="wrap">
        <Box>
          <Typography variant="h3">Alamo Center Dashboard</Typography>
          <Typography variant="body2" color="text.secondary">
            Welcome{user?.full_name ? `, ${user.full_name}` : ''}. Track classes, students, and scheduling activity.
          </Typography>
        </Box>

        <TextField
          size="small"
          placeholder="Search classes, instructors, students…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {loading ? <CircularProgress size={18} /> : <SearchIcon />}
              </InputAdornment>
            )
          }}
        />
      </Box>

      <Box mb={2}>
        <ToggleButtonGroup
          size="small"
          value={dateFilter}
          exclusive
          onChange={(_, v) => v && setDateFilter(v)}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="day">Day</ToggleButton>
          <ToggleButton value="week">Week</ToggleButton>
          <ToggleButton value="month">Month</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Grid container spacing={2}>
        {stats.map((s) => (
          <Grid item xs={12} sm={6} md={3} key={s.key} sx={{ minWidth: 0 }}>
            <Card sx={{ overflow: 'hidden' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                {React.cloneElement(s.icon, { color: s.color })}
                <Box ml={2}>
                  <Typography variant="h4">{s.value}</Typography>
                  <Typography variant="subtitle2" color="text.secondary">
                    {s.label}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}

        <Grid item xs={12} md={6} sx={{ minWidth: 0 }}>
          <Paper sx={{ p: 2, mb: 2, overflow: 'hidden' }}>
            <Typography variant="h6">Scheduled Classes Over Time</Typography>
            <Box sx={{ width: '100%', height: 260, minWidth: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={classesByDay}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip />
                  <Area
                    dataKey="classes"
                    stroke="#1976d2"
                    fill="#1976d2"
                    fillOpacity={0.3}
                    isAnimationActive={false}
                  />
                  <Area
                    dataKey="students"
                    stroke="#9c27b0"
                    fill="#9c27b0"
                    fillOpacity={0.2}
                    isAnimationActive={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6} sx={{ minWidth: 0 }}>
          <Paper sx={{ p: 2, mb: 2, overflow: 'hidden' }}>
            <Typography variant="h6">Class Type Mix</Typography>
            <Box sx={{ width: '100%', height: 260, minWidth: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={classMix}
                    dataKey="value"
                    nameKey="name"
                    outerRadius={90}
                    label
                    isAnimationActive={false}
                  >
                    {classMix.map((entry) => (
                      <Cell
                        key={entry.name}
                        fill={colorMap[entry.name] || '#666'}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6} sx={{ minWidth: 0 }}>
          <Paper sx={{ p: 2, overflow: 'hidden' }}>
            <Typography variant="h6" gutterBottom>
              Upcoming Classes
            </Typography>

            <Stack spacing={1.25}>
              {upcomingSessions.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No upcoming classes found for the selected range.
                </Typography>
              ) : (
                upcomingSessions.map((session) => (
                  <Paper
                    key={session.id}
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderLeft: `6px solid ${colorMap[session.class_name] || '#1976d2'}`
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" gap={2} flexWrap="wrap">
                      <Box>
                        <Typography variant="subtitle1" fontWeight={700}>
                          {session.class_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatDateTime(session.class_date, session.class_time)} • {session.location || 'No location'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Instructor: {session.instructor_name || 'Unassigned'}
                        </Typography>
                      </Box>

                      <Box textAlign="right">
                        <Chip
                          label={`${Number(session.student_count || 0)} students`}
                          size="small"
                          sx={{
                            bgcolor: colorMap[session.class_name] || '#1976d2',
                            color: '#fff'
                          }}
                        />
                      </Box>
                    </Box>
                  </Paper>
                ))
              )}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6} sx={{ minWidth: 0 }}>
          <Paper sx={{ p: 2, overflow: 'hidden' }}>
            <Typography variant="h6">Enrollment by Class</Typography>
            <Box sx={{ width: '100%', height: 320, minWidth: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={enrollmentByClass}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="students" radius={[4, 4, 0, 0]}>
                    {enrollmentByClass.map((entry) => (
                      <Cell
                        key={entry.name}
                        fill={colorMap[entry.name] || '#1976d2'}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={7} sx={{ minWidth: 0 }}>
          <Paper sx={{ p: 2, overflow: 'hidden' }}>
            <Typography variant="h6">Class Schedule</Typography>
            <SavedColumnViewControls
              columns={sessionColumns}
              columnVisibilityModel={sessionColumnVisibilityModel}
              setFieldVisible={setSessionFieldVisible}
              showSimpleView={showSessionSimpleView}
              showFullView={showSessionFullView}
              resetView={resetSessionView}
              label="Columns"
            />
            <Box sx={{ height: 320, width: '100%', minWidth: 0 }}>
              <DataGrid
                rows={sessionRows}
                columns={sessionColumns}
                hideFooter
                disableRowSelectionOnClick
                autoHeight={false}
                columnVisibilityModel={sessionColumnVisibilityModel}
                onColumnVisibilityModelChange={setSessionColumnVisibilityModel}
              />
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={5} sx={{ minWidth: 0 }}>
          <Paper sx={{ p: 2, overflow: 'hidden' }}>
            <Typography variant="h6">Recent Student Activity</Typography>
            <SavedColumnViewControls
              columns={studentColumns}
              columnVisibilityModel={studentColumnVisibilityModel}
              setFieldVisible={setStudentFieldVisible}
              showSimpleView={showStudentSimpleView}
              showFullView={showStudentFullView}
              resetView={resetStudentView}
              label="Columns"
            />
            <Box sx={{ height: 320, width: '100%', minWidth: 0 }}>
              <DataGrid
                rows={studentRows}
                columns={studentColumns}
                hideFooter
                disableRowSelectionOnClick
                autoHeight={false}
                columnVisibilityModel={studentColumnVisibilityModel}
                onColumnVisibilityModelChange={setStudentColumnVisibilityModel}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}